import { enqueueSnackbar } from '~/utils/notifications';
import { useCallback, useEffect } from 'react';
import { useRecoilCallback, useRecoilValue } from 'recoil';
import { DICOMApprovalStatus } from '~/domain/dicom/dicomConstants';
import {
    ContourRedoRequestedEvent,
    ContourUndoRequestedEvent,
    domainEventBus,
    RoiPropertiesUpdatedEvent,
    StructureSetApprovalChangeRequestedEvent,
    StructureSetContourThicknessChangeRequestedEvent,
    StructureSetDescriptionChangeRequestedEvent,
    StructureSetLabelChangeRequestedEvent,
    StructureSetVisibilityChangeRequestedEvent,
} from '~/domain/events';
import { useSessionOrNull } from '~/contexts/SessionContext';
import type { StructureSet } from '~/domain/patient/structureset';
import { createStructureSetReadModel } from '~/domain/readmodels/StructureSetReadModel';
import { saveRequestedState } from '~/state/atoms/saveAtoms';
import {
    currentStructureSetIdState,
    currentStructureSetState,
} from '~/state/atoms/structureSet/structureSetAtoms';

/**
 * Application service layer for {@link StructureSet} operations.
 *
 * Processes structure set domain events by coordinating between domain models,
 * state management, and external systems.
 */
export function useStructureSetHandlers() {
    const currentStructureSetId = useRecoilValue(currentStructureSetIdState);
    const session = useSessionOrNull();
    const handleStructureSetApprovalChangeRequest = useRecoilCallback(
        (recoilInterface) =>
            async (event: StructureSetApprovalChangeRequestedEvent) => {
                try {
                    if (!currentStructureSetId) {
                        console.warn(
                            'No structure set available for approval change'
                        );
                        return;
                    }

                    const { set } = recoilInterface;
                    const structureSet =
                        session?.patient.getStructureSetWithSopInstanceUid(
                            currentStructureSetId
                        );
                    if (!structureSet) {
                        console.warn('Structure set not found');
                        return;
                    }
                    session?.context.tracker.command(() => {
                        structureSet.setApprovalStatus(
                            event.newApprovalStatus,
                            event.reviewerName,
                            event.source === 'user' // Only cascade for user events
                        );
                    });
                    const updatedReadModel =
                        createStructureSetReadModel(structureSet);
                    set(currentStructureSetState, updatedReadModel);

                    // Single owner of the structure set approval
                    // notification: every set-level approval transition is
                    // an explicit user action (the dropdown → confirmation
                    // dialog), so each gets a plain confirmation.
                    if (
                        event.newApprovalStatus === DICOMApprovalStatus.APPROVED
                    ) {
                        enqueueSnackbar(
                            `Structure set approval status was changed to ${DICOMApprovalStatus.APPROVED}.`,
                            { variant: 'success' }
                        );
                    } else if (
                        event.newApprovalStatus === DICOMApprovalStatus.REJECTED
                    ) {
                        enqueueSnackbar(
                            `Structure set approval status was changed to ${DICOMApprovalStatus.REJECTED}.`,
                            {
                                variant: 'warning',
                            }
                        );
                    } else if (
                        event.newApprovalStatus ===
                        DICOMApprovalStatus.UNAPPROVED
                    ) {
                        enqueueSnackbar(
                            `Structure set approval status was changed to ${DICOMApprovalStatus.UNAPPROVED}.`,
                            { variant: 'warning' }
                        );
                    }

                    // Safety measure (mirrors Eclipse): a user-confirmed
                    // approval transition requests an immediate save of
                    // all pending changes, so a clinical sign-off never
                    // exists only in browser memory. The SaveStatusIndicator
                    // consumes the request and owns the outcome UI.
                    if (event.source === 'user') {
                        set(saveRequestedState, (count) => count + 1);
                    }

                    // Emit individual ROI property updated events for each ROI
                    // only when cascading occurred (user events)
                    if (event.source === 'user') {
                        const roiUpdatePromises = updatedReadModel.roiArray.map(
                            (roi) =>
                                domainEventBus.publish(
                                    new RoiPropertiesUpdatedEvent(
                                        roi.structureSetUid,
                                        roi.roiId,
                                        {
                                            approvalStatus:
                                                event.newApprovalStatus,
                                        },
                                        'system'
                                    )
                                )
                        );

                        await Promise.all(roiUpdatePromises);
                    }
                } catch (error) {
                    console.error(
                        `Failed to handle structure set approval change: ${error}`
                    );
                }
            },
        [currentStructureSetId, session]
    );

    // Runs `edit` on the active structure set as one undoable command, then
    // republishes the set's read model, and passes on an edit the set
    // refuses. `what` names the change in the log. The log never names the
    // set or prints the refusal's text: the UID and the label are patient
    // data.
    const editActiveStructureSet = useRecoilCallback(
        ({ set }) =>
            (what: string, edit: (structureSet: StructureSet) => void) => {
                try {
                    if (!currentStructureSetId) {
                        console.warn(`No structure set available for ${what}`);
                        return;
                    }
                    const structureSet =
                        session?.patient.getStructureSetWithSopInstanceUid(
                            currentStructureSetId
                        );
                    if (!structureSet) {
                        console.warn(`Structure set not found for ${what}`);
                        return;
                    }
                    session?.context.tracker.command(() => edit(structureSet));
                    set(
                        currentStructureSetState,
                        createStructureSetReadModel(structureSet)
                    );
                } catch (error) {
                    console.error(`Failed to apply structure set ${what}`);
                    throw error;
                }
            },
        [currentStructureSetId, session]
    );

    const handleStructureSetLabelChangeRequest = useCallback(
        async (event: StructureSetLabelChangeRequestedEvent) =>
            editActiveStructureSet('label change', (structureSet) =>
                structureSet.rename(event.newLabel)
            ),
        [editActiveStructureSet]
    );

    const handleStructureSetDescriptionChangeRequest = useCallback(
        async (event: StructureSetDescriptionChangeRequestedEvent) =>
            editActiveStructureSet('description change', (structureSet) =>
                structureSet.setDescription(event.newDescription)
            ),
        [editActiveStructureSet]
    );

    const handleStructureSetVisibilityChangeRequest = useRecoilCallback(
        (recoilInterface) =>
            async (event: StructureSetVisibilityChangeRequestedEvent) => {
                try {
                    if (!currentStructureSetId) {
                        console.warn(
                            'No structure set available for visibility change'
                        );
                        return;
                    }

                    const { set } = recoilInterface;
                    const structureSet =
                        session?.patient.getStructureSetWithSopInstanceUid(
                            currentStructureSetId
                        );
                    if (!structureSet) {
                        console.warn('Structure set not found');
                        return;
                    }
                    // Tracked seam: emits one coalesced changeset so the
                    // version bumps and version-keyed projections refresh.
                    // The per-ROI engine events below still drive the
                    // viewport (EngineAdapter.onRoiModified does not yet
                    // forward visibility — that consolidation is deferred).
                    session?.structureSetService.setVisibility(
                        { id: structureSet.id },
                        event.visible
                    );
                    const updatedReadModel =
                        createStructureSetReadModel(structureSet);
                    set(currentStructureSetState, updatedReadModel);

                    // Emit individual ROI property updated events for each ROI
                    const roiUpdatePromises = updatedReadModel.roiArray.map(
                        (roi) =>
                            domainEventBus.publish(
                                new RoiPropertiesUpdatedEvent(
                                    roi.structureSetUid,
                                    roi.roiId,
                                    { visible: event.visible },
                                    event.source
                                )
                            )
                    );

                    await Promise.all(roiUpdatePromises);
                } catch (error) {
                    console.error(
                        `Failed to handle structure set visibility change: ${error}`
                    );
                }
            },
        [currentStructureSetId, session]
    );

    const handleStructureSetContourThicknessChangeRequest = useRecoilCallback(
        (recoilInterface) =>
            async (event: StructureSetContourThicknessChangeRequestedEvent) => {
                try {
                    if (!currentStructureSetId) {
                        console.warn(
                            'No structure set available for contour thickness change'
                        );
                        return;
                    }

                    const { set } = recoilInterface;
                    const structureSet =
                        session?.patient.getStructureSetWithSopInstanceUid(
                            currentStructureSetId
                        );
                    if (!structureSet) {
                        console.warn('Structure set not found');
                        return;
                    }
                    // Tracked seam (see the visibility handler): one
                    // coalesced changeset + version bump; the per-ROI engine
                    // events below still drive the viewport.
                    session?.structureSetService.setContourThickness(
                        { id: structureSet.id },
                        event.contourThickness
                    );
                    const updatedReadModel =
                        createStructureSetReadModel(structureSet);
                    set(currentStructureSetState, updatedReadModel);

                    // Emit individual ROI property updated events for each ROI
                    const roiUpdatePromises = updatedReadModel.roiArray.map(
                        (roi) =>
                            domainEventBus.publish(
                                new RoiPropertiesUpdatedEvent(
                                    roi.structureSetUid,
                                    roi.roiId,
                                    {
                                        contourThickness:
                                            event.contourThickness,
                                    },
                                    event.source
                                )
                            )
                    );

                    await Promise.all(roiUpdatePromises);
                } catch (error) {
                    console.error(
                        `Failed to handle structure set contour thickness change: ${error}`
                    );
                }
            },
        [currentStructureSetId, session]
    );

    const handleStructureSetUndoOrRedoRequest = useRecoilCallback(
        (recoilInterface) =>
            (event: ContourUndoRequestedEvent | ContourRedoRequestedEvent) => {
                try {
                    if (!currentStructureSetId) {
                        console.warn('No structure set open');
                        return;
                    }
                    if (event.structureSetUid !== currentStructureSetId) {
                        // Another structure set's undo/redo; the current
                        // read model is unaffected.
                        return;
                    }

                    const { set } = recoilInterface;
                    const structureSet =
                        session?.patient.getStructureSetWithSopInstanceUid(
                            currentStructureSetId
                        );
                    if (!structureSet) {
                        console.warn('Structure set not found');
                        return;
                    }
                    if (!structureSet.getRoiById(event.roiId)) {
                        console.warn(
                            `ROI ${event.roiId} not found in the structure set`
                        );
                        return;
                    }
                    // Undo/redo mutations happen in the engine; rebuild the
                    // read model so React state reflects the new state.
                    set(
                        currentStructureSetState,
                        createStructureSetReadModel(structureSet)
                    );
                } catch (error) {
                    console.error(
                        `Failed to handle structure set visibility change: ${error}`
                    );
                }
            },
        [currentStructureSetId, session]
    );

    useEffect(() => {
        const subscriptions = [
            domainEventBus.subscribe(
                StructureSetApprovalChangeRequestedEvent.EVENT_TYPE,
                handleStructureSetApprovalChangeRequest
            ),
            domainEventBus.subscribe(
                StructureSetLabelChangeRequestedEvent.EVENT_TYPE,
                handleStructureSetLabelChangeRequest
            ),
            domainEventBus.subscribe(
                StructureSetDescriptionChangeRequestedEvent.EVENT_TYPE,
                handleStructureSetDescriptionChangeRequest
            ),
            domainEventBus.subscribe(
                StructureSetVisibilityChangeRequestedEvent.EVENT_TYPE,
                handleStructureSetVisibilityChangeRequest
            ),
            domainEventBus.subscribe(
                StructureSetContourThicknessChangeRequestedEvent.EVENT_TYPE,
                handleStructureSetContourThicknessChangeRequest
            ),
            domainEventBus.subscribe(
                ContourRedoRequestedEvent.EVENT_TYPE,
                handleStructureSetUndoOrRedoRequest
            ),
            domainEventBus.subscribe(
                ContourUndoRequestedEvent.EVENT_TYPE,
                handleStructureSetUndoOrRedoRequest
            ),
        ];

        return () => {
            subscriptions.forEach((sub) => sub.unsubscribe());
        };
    }, [
        handleStructureSetApprovalChangeRequest,
        handleStructureSetLabelChangeRequest,
        handleStructureSetDescriptionChangeRequest,
        handleStructureSetVisibilityChangeRequest,
        handleStructureSetContourThicknessChangeRequest,
        handleStructureSetUndoOrRedoRequest,
    ]);
}
